import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Checkbox, CircularProgress, FormControlLabel, Typography } from '@mui/material';
import { ChangeEvent, FC, useEffect, useState } from 'react';
import { FormProvider, useForm, SubmitHandler } from 'react-hook-form';
import { Alert } from '../../../shared/lib/common/Alert';
import { CustomPhoneController } from './common/CustomPhoneController';
import { PageHeading } from '../../../shared/lib/common/PageHeading';
import { withTrademark } from '../../../shared/lib/common/Tokenizer';
import { INVALID_AUTH_TOKEN, ONBOARDING_ADD_INFO_STRINGS } from '../../../shared/lib/constants';
import palette from '../../../shared/lib/theme/palette';
import { StepperContentWrapper } from '../../../shared/lib/theme/styleConstants';
import { CALLER_ID_ALREADY_ADDED, MSG_PHONE_NUMBER_ADDED_SUCCESSFULLY, MSG_PLEASE_CHECK_YOUR_PHONE, PHONE_ALREADY_VERIFIED, PHONE_NO_CONSENT_TEXT } from '../app/constants';
import { useAppContext } from '../app/contexts/AppContext';
import { PhoneNumberInput, StepperStepsProps } from '../app/interfaceTypes';
import { ActionType } from '../app/reducer/AppContextReducer';
import { addPhoneNumberValidationSchema } from '../app/validationSchema';
import { OtpVerification, UserStatus, useFetchPhoneVerifiedLazyQuery, useSendOtpV2Mutation } from '../generated';
import { VerifyOtp } from './VerifyOtp';
import { useMobileView } from '../app/hooks/useMobileView';

export const AddYourInfo: FC<StepperStepsProps> = ({ handleNext, goToOtp, infoSteps }) => {
  const { dispatch, currentUser, setCurrentUser } = useAppContext();
  const [displayPhoneNumber, setDisplayPhoneNumber] = useState<string>('');
  const [code, setCode] = useState<string | null | undefined>(undefined);
  const mediumViewport = useMobileView();
  const { blackOne } = palette;
  const [isPhoneVerified, setIsPhoneVerified] = useState<boolean>(false);

  const [checked, setChecked] = useState<boolean>(false);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
  };

  const [fetchPhoneVerified, { data, startPolling, stopPolling }] = useFetchPhoneVerifiedLazyQuery({
    onError({ message }) {
      Alert.error(message);
    },
    pollInterval: 5000,

    onCompleted(data) {
      const { me } = data;
      const { phoneVerified, phoneNumberSid } = me;

      if (phoneVerified && phoneNumberSid) {
        setIsPhoneVerified(true);
        stopPolling();
      }
    }
  });

  const [sendOtpV2, { loading }] = useSendOtpV2Mutation({
    onError({ message }) {
      if (message === INVALID_AUTH_TOKEN) Alert.error(INVALID_AUTH_TOKEN);
      else if (message === CALLER_ID_ALREADY_ADDED) {
        Alert.success(PHONE_ALREADY_VERIFIED);
        setIsPhoneVerified(true);
      } else Alert.error(message);
    },

    async onCompleted(data) {
      !isPhoneVerified && (async () => await fetchPhoneVerified())();

      const { sendOtpV2 } = data ?? {};
      const { code } = sendOtpV2 ?? {};

      if (sendOtpV2) {
        setCode(code);
        startPolling(5000);
        dispatch({ type: ActionType.SET_TIME_LEFT, timeLeft: import.meta.env.VITE_SEND_OTP_V2_TIME_LEFT ?? 300 });
        Alert.success(MSG_PLEASE_CHECK_YOUR_PHONE);
        goToOtp?.();
      }
    }
  });

  const methods = useForm<PhoneNumberInput, any, PhoneNumberInput>({
    mode: 'all',
    resolver: yupResolver(addPhoneNumberValidationSchema),

    defaultValues: {
      phoneNumber: ''
    }
  });

  const { handleSubmit, watch } = methods;

  const onSubmit: SubmitHandler<PhoneNumberInput> = async (data) => {
    const { phoneNumber } = data;
    setDisplayPhoneNumber(phoneNumber);

    await sendOtpV2({
      variables: {
        sendOtpInputV2: {
          phoneNumber: phoneNumber.startsWith('+') ? phoneNumber : `+${phoneNumber}`,
          otpType: OtpVerification.PhoneVerify
        }
      }
    });
  };

  useEffect(() => {
    if (data?.me.phoneVerified || isPhoneVerified) {
      const phoneNumber = displayPhoneNumber.startsWith('+') ? displayPhoneNumber : `+${displayPhoneNumber}`;
      const { createdAt, email, emailVerified, id, status, updatedAt, defaultMessage, enablePhoneRinger, showLogo } = currentUser ?? {};
      const updatedUser = {
        ...currentUser,
        phoneVerified: true,
        updatedAt,
        status: status || UserStatus.Active,
        phoneNumber,
        phoneNumberSid: data?.me?.phoneNumberSid,
        id: id || '',
        createdAt: createdAt || '',
        email: email || '',
        emailVerified: !!emailVerified,
        defaultMessage: defaultMessage ?? true,
        enablePhoneRinger: enablePhoneRinger ?? false,
        showLogo: showLogo ?? true
      };

      if (phoneNumber !== null && id) {
        setCurrentUser(updatedUser);
      }

      handleNext?.();
      stopPolling();
      Alert.success(MSG_PHONE_NUMBER_ADDED_SUCCESSFULLY);
    }
  }, [isPhoneVerified, data?.me.phoneVerified]);

  return (
    <Box sx={StepperContentWrapper}>
      <Box height={mediumViewport ? '100%' : 'auto'} width={mediumViewport ? '100%' : '500px'} margin="auto">
        {infoSteps ? (
          <VerifyOtp sendOtp={sendOtpV2} isTpin={false} handleNext={handleNext} infoSteps={infoSteps} displayPhoneNumber={displayPhoneNumber} code={code} />
        ) : (
          <>
            <PageHeading color={blackOne} title={ONBOARDING_ADD_INFO_STRINGS.TITLE} subTitle={ONBOARDING_ADD_INFO_STRINGS.DESCRIPTION} />

            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
                <Box mb={3}>
                  <CustomPhoneController controllerName="phoneNumber" controllerLabel="Phone Number" />

                  <FormControlLabel
                    sx={{
                      mt: 2
                    }}
                    control={<Checkbox checked={checked} onChange={handleChange} />}
                    label={<Typography variant="subtitle2" sx={{ fontSize: 12,textAlign: 'left' }}>{withTrademark(PHONE_NO_CONSENT_TEXT)}</Typography>}
                  />
                </Box>

                <Typography variant="caption" sx={{ mt: 3, mb: 3, color: 'text.secondary', fontStyle: 'italic', display: 'block', textAlign: 'center', fontSize: 12 }}>
                  {withTrademark(ONBOARDING_ADD_INFO_STRINGS.CALL_FROM_ANY_PHONE_INFO)}
                </Typography>

                <Button
                  variant="contained"
                  type="submit"
                  fullWidth
                  color={loading ? 'inherit' : 'primary'}
                  disabled={!checked || loading || (watch('phoneNumber').startsWith('92') ? watch('phoneNumber').length !== 12 : watch('phoneNumber').length !== 11)}
                  endIcon={loading && <CircularProgress size={20} color="inherit" />}
                  sx={{ textTransform: 'none' }}
                >
                  {ONBOARDING_ADD_INFO_STRINGS.ADD_AND_VERIFY}
                </Button>
              </form>
            </FormProvider>
          </>
        )}
      </Box>
    </Box>
  );
};
